import {
  Flex,
  InputText,
  MultiSelectTextInput,
  Select,
  SelectOption,
  Text,
} from '@driftt/dds'
import React from 'react'

import { AVAILABLE_OPERATORS } from 'components/AIConfiguration/LeadQualification/constants'

type ValidValues = string | string[] | undefined

interface BaseComponentProps {
  onChange: any // eslint-disable-line @typescript-eslint/no-explicit-any
  disabled?: boolean
  value: ValidValues
  error: string | undefined
  options?: SelectOption<string | number>[]
}

const {
  GREATER_THAN,
  LESS_THAN,
  IS_BETWEEN,
  EQUAL,
  NOT_EQUAL,
  INCLUDES,
  EXCLUDES,
} = AVAILABLE_OPERATORS

export const DEFAULT_INPUT = ({
  value,
  onChange,
  disabled,
  error,
}: BaseComponentProps) => (
  <InputText
    label="Value"
    disabled={disabled}
    value={value}
    onChange={onChange}
    errorMessage={error}
  />
)

export const DEFAULT_VALIDATION = {
  validate: {
    required: (value: ValidValues) =>
      !value || (value && Array.isArray(value) && value.length === 0)
        ? 'Value required'
        : true,
  },
}

const INPUT_TEXT = {
  component: DEFAULT_INPUT,
  validation: DEFAULT_VALIDATION,
  defaultValue: '',
}

const SELECT_OR_TEXT = {
  component: ({
    value,
    onChange,
    disabled,
    error,
    options,
  }: BaseComponentProps) =>
    options && options.length > 0 ? (
      <Select
        label="Value"
        options={options}
        onChange={(newValue) => onChange((newValue as SelectOption).value)}
        isDisabled={disabled}
        value={
          typeof value === 'string'
            ? {
                label: value,
                value,
              }
            : value
        }
        error={error}
      />
    ) : (
      <InputText
        label="Value"
        disabled={disabled}
        value={value}
        onChange={onChange}
        errorMessage={error}
      />
    ),
  validation: DEFAULT_VALIDATION,
  defaultValue: '',
}

const MULTI_SELECT = {
  component: ({
    value,
    onChange,
    disabled,
    error,
    options,
  }: BaseComponentProps) =>
    options && options.length > 0 ? (
      <Select
        label="Value"
        options={options}
        onChange={(newValue) => {
          onChange(
            (newValue as SelectOption<string | number>[]).map(
              ({ value }) => value
            )
          )
        }}
        multi
        isDisabled={disabled}
        value={
          value && Array.isArray(value)
            ? value.map((v: string) => ({ label: v, value: v }))
            : []
        }
        error={error}
      />
    ) : (
      <MultiSelectTextInput
        label="Value"
        description="Press enter after each attribute"
        onChange={(newValue) => {
          onChange({
            target: {
              value: newValue.map(({ value }) => value),
            },
          })
        }}
        initialValue={
          value && Array.isArray(value)
            ? value.map((v: string) => ({ label: v, value: v }))
            : []
        }
        isDisabled={disabled}
        error={error}
      />
    ),
  validation: DEFAULT_VALIDATION,
  defaultValue: [],
}

const RANGE_INPUT_TEXT = {
  component: ({ value, onChange, disabled, error }: BaseComponentProps) => {
    const commonProps = {
      size: 'full-width' as const,
      errorMessage: error,
      disabled: disabled,
    }

    return (
      <Flex gap={1} grow={0}>
        <Flex css={{ width: 121 }}>
          <InputText
            onChange={(event) => onChange([event.target.value, value?.[1]])}
            value={value?.[0] || ''}
            label="Start Value"
            {...commonProps}
          />
        </Flex>

        <Text css={{ paddingTop: '32px' }} variant="metadata">
          and
        </Text>

        <Flex css={{ width: 121 }}>
          <InputText
            onChange={(event) => onChange([value?.[0], event.target.value])}
            value={value?.[1] || ''}
            label="End Value"
            {...commonProps}
          />
        </Flex>
      </Flex>
    )
  },
  validation: {
    validate: {
      required: (values: ValidValues) =>
        Boolean(values?.length !== 2 || values[0] === '' || values[1] === '')
          ? 'Value required'
          : true,
    },
  },
  defaultValue: ['', ''],
}

export const OPERATOR_COMPONENTS_AND_VALIDATIONS = {
  [GREATER_THAN]: INPUT_TEXT,
  [LESS_THAN]: INPUT_TEXT,
  [EQUAL]: SELECT_OR_TEXT,
  [NOT_EQUAL]: INPUT_TEXT,
  [INCLUDES]: MULTI_SELECT,
  [EXCLUDES]: MULTI_SELECT,
  [IS_BETWEEN]: RANGE_INPUT_TEXT,
}
